import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  FaChevronRight,
  FaChevronDown,
  FaCopy,
  FaFilter,
} from "react-icons/fa";
import ContextMenu from "./ContextMenu";
import { useToast } from "../common/ToastContext";

const ChecklistDropdownInput = ({
  category,
  propertyKey,
  value,
  checklistItems,
  handleChange,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const { showToast } = useToast();

  // Parse the comma-separated value into an array of selected items
  const selectedValues = useMemo(() => {
    return value ? value.split(",").map((v) => v.trim()) : [];
  }, [value]);

  // Handle checkbox toggling - this function updates the parent component state
  const toggleItem = (item) => {
    if (item.isCategory) return; // Don't toggle categories
    if (item.disabled) return; // Don't toggle disabled items

    let newSelectedValues;
    const itemValue = item.value || item;

    if (selectedValues.includes(itemValue)) {
      // Remove item
      newSelectedValues = selectedValues.filter((v) => v !== itemValue);
    } else {
      // Add item
      newSelectedValues = [...selectedValues, itemValue];
    }

    // Join with commas and update
    const newValue = newSelectedValues.join(", ");
    handleChange(category, propertyKey, newValue);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Create menu items directly with custom render function
  const menuItems = checklistItems.map((item) => {
    // For category items
    if (item.isCategory) {
      return {
        ...item,
        render: () => (
          <div className="px-2 py-1 text-xs font-semibold text-gray-400 border-b border-gray-700">
            {item.label}
          </div>
        ),
        preventAutoClose: true,
      };
    }

    // For checkable items
    const itemValue = item.value || item;
    const isSelected = selectedValues.includes(itemValue) || item.disabled;

    return {
      ...item,
      preventAutoClose: true,
      onClick: (e) => {
        e?.stopPropagation?.();
        toggleItem(item);
      },
      render: () => (
        <div
          className={`flex items-center px-2 py-1 ${item.disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-600 cursor-pointer'} text-xs text-gray-300 w-full box-border`}
          onClick={(e) => {
            e.stopPropagation();
            toggleItem(item);
          }}
          title={item.label + "\n  " + item.tooltip + (item.disabled ? "\n  (Always enabled)" : "")}
        >
          <div className="mr-2 relative flex items-center justify-center">
            <div
              className={`w-3.5 h-3.5 ${
                isSelected
                  ? "bg-blue-600 border-blue-400"
                  : "bg-gray-800/80 border-gray-500"
              } rounded`}
            >
              {isSelected && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
          </div>
          <span className="flex-grow break-words whitespace-normal">
            {(item.label || item).toString().replace(/_/g, " ")}
          </span>
          {item.example && (
            <FaCopy
              size={10}
              className="ml-2 text-gray-300 hover:text-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(item.example);
                showToast("Copied example", "info");
              }}
              title="Copy example"
            />
          )}
        </div>
      ),
    };
  });

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center">
        <button
          ref={buttonRef}
          className={`flex-grow bg-transparent text-white text-xs px-1 py-1 focus:outline-none text-left flex justify-between items-center ${
            readOnly ? "cursor-not-allowed opacity-50" : ""
          }`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={readOnly}
        >
          <span
            className="truncate min-w-[100px] max-w-[175px]"
            title={
              selectedValues.length > 0
                ? "[\n" + selectedValues.join(",\n") + "\n]"
                : "[]"
            }
          >
            {selectedValues.length > 0
              ? "[" + selectedValues.join(", ") + "]"
              : "[]"}
          </span>
          <FaChevronDown
            className="text-gray-300/80 ml-4 flex-shrink-0"
            size={10}
          />
        </button>
      </div>

      {isOpen && buttonRef.current && (
        <div className="absolute z-50 mt-1 w-full bg-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto overflow-x-hidden dark-slim-scrollbar">
          <div className="py-1 px-0.5 whitespace-nowrap">
            {menuItems.map((item, index) => (
              <div key={index}>{item.render()}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DropdownInput = ({
  category,
  propertyKey,
  value,
  menuItems,
  handleChange,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const { showToast } = useToast();

  const items = menuItems.map((item) => ({
    label: item.label || item,
    value: item.value || item,
    onClick: () => {
      handleChange(category, propertyKey, item.value || item);
      setIsOpen(false);
    },
    tooltip: item.tooltip,
    example: item.example,
  }));

  const selectedItem = items.find((item) => item.value === value);

  const renderItem = (item) => (
    <div
      className="flex items-center justify-between w-full"
      title={item.tooltip}
    >
      <span>{item.label}</span>
      {item.example && (
        <FaCopy
          size={10}
          className="ml-2 text-gray-300"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(item.example);
            showToast("Copied example", "info");
          }}
          title="Copy example"
        />
      )}
    </div>
  );

  return (
    <div className="relative">
      <div className="flex items-center">
        <button
          ref={buttonRef}
          className={`flex-grow bg-transparent text-white text-xs px-1 py-1 focus:outline-none text-left flex justify-between items-center ${
            readOnly ? "cursor-not-allowed opacity-50" : ""
          }`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={readOnly}
        >
          <span className="min-w-[100px]">
            {selectedItem ? selectedItem.label : "Select an option"}
          </span>
          <FaChevronDown className="text-gray-300/80 ml-4" size={10} />
        </button>
        {selectedItem && selectedItem.example && (
          <button
            className="ml-2 text-blue-400 hover:text-blue-300"
            onClick={() => {
              navigator.clipboard.writeText(selectedItem.example);
              showToast("Copied example", "info");
            }}
          >
            <FaCopy size={12} />
          </button>
        )}
      </div>
      {isOpen && buttonRef.current && (
        <ContextMenu
          items={items}
          x={buttonRef.current.getBoundingClientRect().left}
          y={buttonRef.current.getBoundingClientRect().bottom}
          onClose={() => setIsOpen(false)}
          renderItem={renderItem}
        />
      )}
    </div>
  );
};

const PropertiesControl = forwardRef(
  (
    {
      properties,
      onPropertyChange,
      initiallyExpanded = true,
      readOnly = false,
      categoryAliases = {},
    },
    ref
  ) => {
    const [expandedCategories, setExpandedCategories] = useState({});
    const [hoveredRow, setHoveredRow] = useState(null);
    const textareaRefs = useRef({});
    const { showToast } = useToast();

    useEffect(() => {
      setExpandedCategories(
        Object.keys(properties).reduce((acc, category) => {
          if (initiallyExpanded) {
            acc[category] = true;
          }
          return acc;
        }, {})
      );
    }, [properties, initiallyExpanded]);

    useEffect(() => {
      Object.values(textareaRefs.current).forEach(adjustTextareaHeight);
    }, [properties, expandedCategories]);

    const toggleCategory = useCallback((category) => {
      setExpandedCategories((prev) => ({
        ...prev,
        [category]: !prev[category],
      }));
    }, []);

    const toggleAllCategories = useCallback(
      (expand) => {
        setExpandedCategories(
          Object.keys(properties).reduce((acc, category) => {
            acc[category] = expand;
            return acc;
          }, {})
        );
      },
      [properties]
    );

    const showProperty = useCallback(
      (propertyName) => {
        onPropertyChange(
          "Properties",
          propertyName,
          properties.Properties[propertyName].value,
          true
        );
      },
      [onPropertyChange, properties]
    );

    useImperativeHandle(ref, () => ({
      toggleAllCategories,
      showProperty,
    }));

    const handleChange = useCallback(
      (category, key, value) => {
        if (onPropertyChange) {
          onPropertyChange(category, key, value);
        }
      },
      [onPropertyChange]
    );

    const adjustTextareaHeight = (textarea) => {
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height =
          Math.min(textarea.scrollHeight, 7.5 * 16) + "px";
      }
    };

    const renderPropertyInput = useCallback(
      (category, key, value) => {
        if (value.visible === false) return null;

        const handleFilterClick = (e) => {
          e.stopPropagation();
          window.dispatchEvent(
            new CustomEvent("setSearchBoxTerm", {
              detail: { searchTerm: value.value, isRegExMode: false },
            })
          );
        };

        const handleCopyClick = (e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(value.value || "");
          showToast("Copied to clipboard", "info");
        };

        const copyIcon = (
          <button
            className={`ml-2 text-blue-400 hover:text-blue-300 ${
              hoveredRow === `${category}-${key}` ? "opacity-100" : "opacity-0"
            } transition-opacity duration-200 ease-in-out`}
            onClick={handleCopyClick}
            title="Copy value"
          >
            <FaCopy size={12} />
          </button>
        );

        const filterIcon = value.filter ? (
          <button
            className={`ml-2 text-blue-400 hover:text-blue-300 ${
              hoveredRow === `${category}-${key}` ? "opacity-100" : "opacity-0"
            }`}
            onClick={handleFilterClick}
            title="Filter by this property"
          >
            <FaFilter size={12} />
          </button>
        ) : null;

        // Add explicit condition for checklistItems
        if (value.checklistItems) {
          return (
            <div className="flex items-center">
              <ChecklistDropdownInput
                category={category}
                propertyKey={key}
                value={value.value}
                checklistItems={value.checklistItems}
                handleChange={handleChange}
                readOnly={readOnly || value.readOnly}
              />
              {filterIcon}
            </div>
          );
        } else if (value.menuItems) {
          return (
            <div className="flex items-center">
              <DropdownInput
                category={category}
                propertyKey={key}
                value={value.value}
                menuItems={value.menuItems}
                handleChange={handleChange}
                readOnly={readOnly || value.readOnly}
              />
              {filterIcon}
            </div>
          );
        } else {
          return (
            <div className="flex items-center">
              <textarea
                ref={(el) => (textareaRefs.current[`${category}-${key}`] = el)}
                value={value.value ?? ""}
                onChange={(e) => {
                  handleChange(category, key, e.target.value);
                  adjustTextareaHeight(e.target);
                }}
                className={`flex-grow bg-transparent text-white text-xs px-1 focus:outline-none resize-none overflow-x-hidden overflow-y-auto ${
                  value.readOnly || readOnly
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
                rows="1"
                style={{
                  minHeight: "1.5em",
                  height: "auto",
                  maxHeight: "15rem",
                }}
                readOnly={value.readOnly || readOnly}
              />
              {value.example && (
                <button
                  className="ml-2 text-blue-400 hover:text-blue-300"
                  onClick={() => {
                    navigator.clipboard.writeText(value.example);
                    showToast("Copied example", "info");
                  }}
                  title="Copy example"
                >
                  <FaCopy size={12} />
                </button>
              )}
              {copyIcon}
              {filterIcon}
            </div>
          );
        }
      },
      [handleChange, readOnly, showToast, hoveredRow]
    );

    return (
      <div className="text-gray-100 overflow-x-hidden overflow-y-auto font-sans">
        <table className="w-full border-collapse">
          <tbody>
            {Object.entries(properties).map(([category, items]) => (
              <React.Fragment key={category}>
                <tr
                  className="bg-gray-750 cursor-pointer"
                  onClick={() => toggleCategory(category)}
                >
                  <td
                    colSpan="2"
                    className="p-2 font-thin text-blue-300 border-b border-gray-600"
                  >
                    <div className="flex items-center text-sm">
                      {expandedCategories[category] ? (
                        <FaChevronDown
                          className="text-blue-400 mr-2"
                          size={10}
                        />
                      ) : (
                        <FaChevronRight
                          className="text-blue-400 mr-2"
                          size={10}
                        />
                      )}
                      {categoryAliases[category] || category}
                    </div>
                  </td>
                </tr>
                {expandedCategories[category] &&
                  Object.entries(items).map(([key, value]) => {
                    if (value.visible === false) return null;
                    return (
                      <tr
                        key={key}
                        className="hover:bg-gray-700 align-top"
                        onMouseEnter={() => setHoveredRow(`${category}-${key}`)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ height: "auto" }}
                      >
                        <td className="pl-6 p-1 text-xs text-gray-300/80 border-b border-gray-600">
                          <span title={value.tooltip}>{key}</span>
                        </td>
                        <td className="p-1 border-b border-gray-600 align-top w-full break-words">
                          <div className="break-all max-h-[500px]">
                            {renderPropertyInput(category, key, value)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

export default PropertiesControl;
